import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Edit, FolderSync, RefreshCw, Trash2 } from 'lucide-react';

import { BackButton } from '@/components/ui/BackButton';
import { Button } from '@/components/ui/button';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { DateDisplay } from '@/components/date-display';
import { Badge } from '@/components/ui/badge';
import { Product } from '@tagadapay/crm-entities';
import { DeleteProductModal } from '../../components/delete-product-modal';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { useSyncOneProduct, useSyncOneProductSellingPlans } from '@/lib/api/products';

interface ProductHeaderProps {
  product: Product.ProductWithRelations;
}

export const ProductHeader = ({ product }: ProductHeaderProps) => {
  const { mutate: syncOneProduct, isPending: isSyncingOneProduct } = useSyncOneProduct();
  const { mutate: syncOneProductSellingPlans, isPending: isSyncingSellingPlans } =
    useSyncOneProductSellingPlans();
  const { storeRoutes, navigate, id: storeId } = useStoreNavigate();
  const { t } = useTranslation('products');
  const { t: tCommon } = useTranslation('common');
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const handleDeleteSuccess = () => {
    navigate(storeRoutes.PRODUCTS());
  };

  return (
    <>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-4">
          <BackButton to={storeRoutes.PRODUCTS()} />
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">{product.name}</h1>
              <Badge variant={product.active ? 'default' : 'secondary'}>
                {product.active ? t('active') : t('inactive')}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1 text-sm">
              <DateDisplay value={product.createdAt} />
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
          <CopyableResourceBadge name="Product ID" value={product.id} variant="outline" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => syncOneProduct({ productId: product.id })} disabled={isSyncingOneProduct}>
              <FolderSync className="mr-2 h-4 w-4" />
              {isSyncingOneProduct ? tCommon('syncing') : tCommon('sync')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => syncOneProductSellingPlans({ productId: product.id })}
              disabled={isSyncingSellingPlans}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isSyncingSellingPlans ? 'animate-spin' : ''}`} />
              {isSyncingSellingPlans ? t('syncingSellingPlans') : t('syncSellingPlans')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowDeleteModal(true)}>
              <Trash2 className="mr-2 h-4 w-4" />
              {tCommon('delete')}
            </Button>
            <Button size="sm" onClick={() => navigate(storeRoutes.PRODUCT_EDIT(product.id))}>
              <Edit className="mr-2 h-4 w-4" />
              {tCommon('edit')}
            </Button>

          </div>
        </div>
      </div>

      {showDeleteModal && (
        <DeleteProductModal
          productId={product.id}
          onClose={() => setShowDeleteModal(false)}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </>
  );
};
